import { useMutation, UseMutationOptions } from "@tanstack/react-query"
import { AxiosError } from "axios"
import z from "zod"

import { LibraryResponses } from "@tfg/types/endpoints/library"
import { userUpdateProfileSchema } from "@tfg/types/schemas/user"
import { standardEmail } from "@tfg/types/schemas/utils"

import { httpClient } from "~/backend/axios"
import { queryClient } from "~/backend/queryClient"
import { i18n } from "~/i18n"

export const loginSchema = z.object({
  username: standardEmail(),
  password: z.string(),
  recaptchaToken: z.string().optional(),
})

export type LoginForm = z.infer<typeof loginSchema>

export const useLogin = (
  options?: UseMutationOptions<LibraryResponses.Login, AxiosError, LoginForm>
) =>
  useMutation<LibraryResponses.Login, AxiosError, LoginForm>({
    mutationKey: ["auth", "login"],

    mutationFn: async (variables) => {
      const response = await httpClient.post<LibraryResponses.Login>(
        "auth/login/password",
        variables
      )
      return response.data
    },

    ...options,

    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({ queryKey: ["auth"] })
      await options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })

export const useUsernameMutation = (
  options?: UseMutationOptions<
    LibraryResponses.Username,
    AxiosError,
    { username: string }
  >
) =>
  useMutation<LibraryResponses.Username, AxiosError, { username: string }>({
    mutationKey: ["auth", "username"],

    mutationFn: async (variables) => {
      const response = await httpClient.post("auth/login/username", variables)
      return response.data
    },

    ...options,

    onSuccess: async (data, variables, onMutateResult, context) => {
      await options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })

export const useForgotPasswordMutation = (
  options?: UseMutationOptions<void, AxiosError, { email: string }>
) =>
  useMutation<void, AxiosError, { email: string }>({
    mutationKey: ["auth", "forgotPassword"],

    mutationFn: async (variables) => {
      const response = await httpClient.post("auth/reset-password", variables)
      return response.data
    },

    onSuccess: async (data, variables, onMutateResult, context) => {
      await options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })

export const useMagicLinkMutation = (
  options?: UseMutationOptions<void, AxiosError, { email: string }>
) =>
  useMutation<void, AxiosError, { email: string }>({
    mutationKey: ["auth", "magicLink"],

    mutationFn: async (variables) => {
      const response = await httpClient.post("auth/magic-link", variables)
      return response.data
    },

    onSuccess: async (data, variables, onMutateResult, context) => {
      await options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })

const basePasswordSchema = z
  .string()
  .regex(/[a-z]/)
  .regex(/[A-Z]/)
  .regex(/[0-9]/)

export const userPasswordSchema = basePasswordSchema.min(12)
export const adminPasswordSchema = basePasswordSchema.min(16)

export const passwordSchema = z
  .object({
    password: userPasswordSchema,
    confirm: z.string(),
  })
  .refine((data) => data.password === data.confirm, {
    message: i18n.t("profile.passwordsDoNotMatch", { ns: "account" }),
    path: ["confirm"],
  })

export type PasswordForm = z.infer<typeof passwordSchema>
export type ProfileForm = z.infer<typeof userUpdateProfileSchema>

export const useUpdateProfileMutation = (
  options?: UseMutationOptions<void, AxiosError, ProfileForm>
) => {
  return useMutation<void, AxiosError, ProfileForm>({
    mutationKey: ["auth", "profile"],

    mutationFn: async (variables) => {
      await httpClient.patch("users/profile", variables)
    },

    onSuccess: async (data, variables, onMutateResult, context) => {
      await options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

export const useUpdatePasswordMutation = (
  options?: UseMutationOptions<void, AxiosError, PasswordForm>
) => {
  return useMutation<void, AxiosError, PasswordForm>({
    mutationKey: ["auth", "profile"],

    mutationFn: async (variables) => {
      await httpClient.patch("users/password", variables)
    },

    onSuccess: async (data, variables, onMutateResult, context) => {
      await options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

export const twoFactorSchema = z.object({
  userId: z.string().optional(),
  token: z.string(),
})

export type TwoFactorForm = z.infer<typeof twoFactorSchema>

type ConfigureTwoFactorResponse = {
  url: string
}

export const useConfigureTwoFactorMutation = (
  options?: UseMutationOptions<ConfigureTwoFactorResponse, AxiosError, void>
) =>
  useMutation<ConfigureTwoFactorResponse, AxiosError, void>({
    mutationKey: ["auth", "config2fa"],

    mutationFn: async (variables) => {
      const response = await httpClient.post("auth/config2fa", variables)
      return response.data
    },

    onSuccess: async (data, variables, onMutateResult, context) => {
      await options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })

export const useVerifyTwoFactorMutation = (
  options?: UseMutationOptions<
    LibraryResponses.TFGUser,
    AxiosError,
    TwoFactorForm
  >
) =>
  useMutation<LibraryResponses.TFGUser, AxiosError, TwoFactorForm>({
    mutationKey: ["auth", "verify2fa"],

    mutationFn: async (variables) => {
      const response = await httpClient.post("auth/verify2fa", variables)
      return response.data
    },

    onSuccess: async (data, variables, onMutateResult, context) => {
      await options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
